import { useState, useCallback, useEffect } from 'react';

// Simple in-memory cache with TTL
const cacheStore = new Map(); // key -> { data, expiresAt }

const getCacheKey = (apiFunc, args) => {
	try {
		return `${apiFunc?.name || 'anon'}::${JSON.stringify(args || [])}`;
	} catch {
		return `${apiFunc?.name || 'anon'}::fallback-key`;
	}
};

export const prefetch = async (apiFunc, args = [], { ttlMs = 2 * 60 * 1000 } = {}) => {
	const key = getCacheKey(apiFunc, args);
	const existing = cacheStore.get(key);
	const now = Date.now();
	if (existing && existing.expiresAt > now) {
		return existing.data;
	}
	const data = await apiFunc(...args);
	cacheStore.set(key, { data, expiresAt: now + ttlMs });
	return data;
};

const useFetchData = (apiFunc, args = [], dependencies = [], options = {}) => {
	const { useCache = true, ttlMs = 5 * 60 * 1000, onSuccess } = options;
	const [loading, setLoading] = useState(false);
	const [response, setResponse] = useState(null);
	const [error, setError] = useState(null);

	const callApi = useCallback(async (...callArgs) => {
		setLoading(true);
		setError(null);
		try {
			const res = await apiFunc(...callArgs);
			if (res?.code === 400) {
				throw new Error(res?.message);
			} else {
				setResponse(res);
				if (typeof onSuccess === 'function') {
					try { onSuccess(res); } catch {}
				}
				if (useCache) {
					const key = getCacheKey(apiFunc, callArgs);
					cacheStore.set(key, { data: res, expiresAt: Date.now() + ttlMs });
				}
			}
		} catch (err) {
			setError(err);
		} finally {
			setLoading(false);
		}
	}, [apiFunc, ttlMs, useCache, onSuccess]);

	useEffect(() => {
		// Only call API if first arg (typically the required ID) is defined
		// Other args can be null/undefined as they might be optional params
		if (args.length > 0 && args[0] !== undefined && args[0] !== null) {
			let didUseCache = false;
			if (useCache) {
				const key = getCacheKey(apiFunc, args);
				const cached = cacheStore.get(key);
				const now = Date.now();
				if (cached && cached.expiresAt > now) {
					setResponse(cached.data);
					setLoading(false);
					didUseCache = true;
					if (typeof onSuccess === 'function') {
						try { onSuccess(cached.data); } catch {}
					}
				}
			}
			if (!didUseCache) {
				callApi(...args);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, dependencies);

	return { loading, response, error, callApi };
};

export default useFetchData;